import { FormEvent, useEffect, useState } from "react";
import type { SessionUser } from "@platform/contracts";
import { api } from "./api";
import { BrandMark, DesignerCredit } from "./components/BrandMark";
import { ThemeSwitcher } from "./components/ThemeSwitcher";
import { Portal } from "./components/Portal";
import { PunchPage } from "./components/PunchPage";
import { applyTheme, THEME_KEY, type ThemePreference } from "./theme";

export function App() {
  const [theme, setTheme] = useState<ThemePreference>(() => (localStorage.getItem(THEME_KEY) as ThemePreference | null) ?? "system");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => { applyTheme(theme); }, [theme]);
  useEffect(() => {
    const media = matchMedia("(prefers-color-scheme: dark)");
    const update = () => theme === "system" && applyTheme("system");
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [theme]);
  const punchTerminal = /(^|\/)punch\/?$/.test(window.location.pathname) || window.location.hash.replace(/^#\/?/, "") === "punch" || new URLSearchParams(window.location.search).has("punch");
  useEffect(() => {
    const expired = () => setUser((current) => { if (current) setError("Your session timed out after a period of inactivity. Please sign in again."); return null; });
    window.addEventListener("platform:session-expired", expired);
    return () => window.removeEventListener("platform:session-expired", expired);
  }, []);
  useEffect(() => { if (punchTerminal) { setLoading(false); return; } api.session().then((result) => setUser(result.user ?? null)).catch(() => undefined).finally(() => setLoading(false)); }, [punchTerminal]);

  async function login(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError(""); setSubmitting(true);
    const data = new FormData(event.currentTarget);
    try { const result = await api.login({ email: String(data.get("email")), password: String(data.get("password")) }); setUser(result.user); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Unable to sign in"); }
    finally { setSubmitting(false); }
  }
  if (punchTerminal) return <PunchPage />;
  if (loading) return <main className="loading"><BrandMark /><span className="spinner" /><span>Securing your workspace…</span></main>;
  if (user) return <Portal user={user} theme={theme} onThemeChange={setTheme} onLogout={() => api.logout().then(() => setUser(null))} />;

  return <main className="login-page">
    <section className="brand-panel">
      <header><BrandMark hero /><ThemeSwitcher value={theme} onChange={setTheme} /></header>
      <div className="brand-copy"><span className="eyebrow"><i /> Private operations</span><h1>Everything in motion.<br/><em>One place.</em></h1><p>Ordering, fulfilment, delivery and reporting. Connected in one secure workspace built for your team.</p></div>
    </section>
    <section className="login-panel">
      <div className="mobile-brand"><BrandMark hero /><ThemeSwitcher value={theme} onChange={setTheme} /></div>
      <form className="login-card" onSubmit={login}>
        <span className="eyebrow">Welcome back</span><h2>Sign in to your workspace</h2><p>Enter your credentials to access the operations portal.</p>
        <label>Email address<input name="email" type="email" autoComplete="username" placeholder="name@company.com" required /></label>
        <label>Password <a href="mailto:system-administrator">Forgot password?</a><span className="password-field"><input name="password" type={showPassword ? "text" : "password"} autoComplete="current-password" placeholder="Enter your password" minLength={12} required /><button type="button" className="password-reveal" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword((current) => !current)}>{showPassword
          ? <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 3l18 18"/><path d="M10.6 10.6a2 2 0 0 0 2.8 2.8"/><path d="M9.4 5.2A9.6 9.6 0 0 1 12 5c5 0 9 4.5 9 7 0 .9-.7 2.2-1.9 3.4M6.5 6.9C4.4 8.3 3 10.3 3 12c0 2.5 4 7 9 7 1.4 0 2.7-.3 3.8-.9"/></svg>
          : <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 12s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7Z"/><circle cx="12" cy="12" r="2.6"/></svg>}</button></span></label>
        {error && <div className="form-error" role="alert">{error}</div>}
        <button className="primary-button" disabled={submitting}>{submitting ? "Signing in…" : "Sign in securely"}<span aria-hidden="true">→</span></button>
        <small className="support">Need access? Contact your system administrator.</small>
      </form>
      <footer>© 2026 Tal-Ahwa <span>Privacy</span><span>Security</span></footer>
      <DesignerCredit />
    </section>
  </main>;
}
